import clsx from "clsx";
import { useMemo } from "react";
import { NavigateOptions } from "react-router-dom";

import {
  Card,
  ClassificationCounters,
  KeyValueList,
  RenderWhen,
  ThreatStatusLabel
} from "../../../../../../components";
import useParanoidMode from "../../../../../paranoid-mode/hooks/useParanoidMode";
import { SECTIONS } from "../../../../consts";
import { ANALYSIS } from "../../../report_summary/consts";
import { useNetworkReferences } from "../../../report_summary/hooks/";
import { NETWORK_REFERENCES_ID } from "../../../summary/NetworkReferencesContainer";
import { CardSubtitle, Loading } from "../../components";
import WarningIcon from "../../components/icons/WarningIcon";
import NetworkReferencesValue from "./NetworkReferencesValue";

import { formatCount } from "../../../../../../components/PageResultsCount";
import { DYNAMIC_ANALYSIS_TABS_ID } from "../../../common/dynamicanalysis/network/AnalysisTabs";
import styles from "../analysis-card.module.scss";
import { getNetworkReferencesCounts } from "./utils";

import { enums } from "../../../../../../shim/jsbridge";

type Props = {
  onClick: (section: string, options?: NavigateOptions) => void;
};

const { paranoidModeComponents } = enums ?? {};

const NetworkReferences = ({ onClick }: Props) => {
  const { data, stats, dataReady } = useNetworkReferences();
  const { paranoidModeConfig } = useParanoidMode();

  const counts = useMemo(() => getNetworkReferencesCounts(data), [data]);
  const totalMalicious = counts.malicious;

  const { included = [], level } = paranoidModeConfig ?? {};

  const handleNetworkReferenceClick = (section: string) => {
    onClick(section, { state: { scrollTo: DYNAMIC_ANALYSIS_TABS_ID } });
  };

  const isNWParanoidMode =
    !!level && included.includes(paranoidModeComponents.NETWORK_REFERENCES);

  return (
    <Card
      disabled={!dataReady}
      icon="network"
      title="Network Analysis"
      subtitle={
        <CardSubtitle hasData impactsFinalClassification={isNWParanoidMode}>
          <RenderWhen condition={dataReady} fallback={<Loading />}>
            <ThreatStatusLabel
              className={styles.analysis_card_subtitle_classification}
              labelText={`${formatCount(totalMalicious ?? 0)}/${formatCount(
                data?.length ?? 0
              )} Malicious`}
              threatStatus={totalMalicious ? "malicious" : "unknown"}
              showTitle={false}
              showOrb={false}
              showColor
            />
          </RenderWhen>
        </CardSubtitle>
      }
      data-testid="report_summary_network_analysis"
    >
      <KeyValueList
        className={styles.analysis_card_content_wrapper}
        onClick={event => {
          event?.stopPropagation();
        }}
        data-testid="report_summary_network_analysis_list"
      >
        <KeyValueList.Item
          className={clsx(
            styles.list_item,
            styles.network_references,
            stats.total && styles.active
          )}
          keyClassName="font-medium"
          label="Network References: "
          value={
            <div className="flex align-center flex-gap-1">
              <ClassificationCounters
                className={styles.classification_counters}
                counts={counts}
              />
              <RenderWhen condition={stats.hasMoreData}>
                <WarningIcon
                  message={`Analysis based on the first ${formatCount(
                    data.length ?? 0
                  )} network references.`}
                />
              </RenderWhen>
            </div>
          }
          onClick={() => {
            if (!stats.total) return;
            onClick(SECTIONS.FILE_ANALYSIS.NETWORK_REFERENCES, {
              state: { scrollTo: NETWORK_REFERENCES_ID }
            });
          }}
          data-testid="report_summary_network_analysis_references"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.TICORE].total && styles.active
          )}
          keyClassName="font-medium"
          label="Spectra Core: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.TICORE].malicious}
              total={stats[ANALYSIS.TICORE].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.TICORE].total) return;
            onClick(SECTIONS.TI_CORE.INTERESTING_STRINGS);
          }}
          data-testid="report_summary_network_analysis_spectra_core"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.RLCS].total && styles.active
          )}
          keyClassName={clsx("font-medium")}
          label="RL Cloud Sandbox: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.RLCS].malicious}
              total={stats[ANALYSIS.RLCS].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.RLCS].total) return;
            handleNetworkReferenceClick(
              SECTIONS.DYNAMIC_ANALYSIS.CLOUD_SANDBOX_NETWORK
            );
          }}
          data-testid="report_summary_network_analysis_spectra_sandbox"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.RLAA].total && styles.active
          )}
          keyClassName={clsx("font-medium")}
          label="Auxiliary Analysis: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.RLAA].malicious}
              total={stats[ANALYSIS.RLAA].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.RLAA].total) return;
            handleNetworkReferenceClick(
              SECTIONS.DYNAMIC_ANALYSIS.AUXILIARY_ANALYSIS_NETWORK
            );
          }}
          data-testid="report_summary_network_analysis_auxiliary_analysis"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.CAPE].total && styles.active
          )}
          keyClassName="font-medium"
          label="CAPE Sandbox: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.CAPE].malicious}
              total={stats[ANALYSIS.CAPE].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.CAPE].total) return;
            handleNetworkReferenceClick(SECTIONS.DYNAMIC_ANALYSIS.CAPE_NETWORK);
          }}
          data-testid="report_summary_network_analysis_cape"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.CISCO_SMA].total && styles.active
          )}
          keyClassName="font-medium"
          label="Cisco SMA: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.CISCO_SMA].malicious}
              total={stats[ANALYSIS.CISCO_SMA].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.CISCO_SMA].total) return;
            handleNetworkReferenceClick(
              SECTIONS.DYNAMIC_ANALYSIS.CISCO_SMA_NETWORK
            );
          }}
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.MEDIAADDRESS]?.total && styles.active
          )}
          keyClassName="font-medium"
          label="Quishing Analysis: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.MEDIAADDRESS]?.malicious}
              total={stats[ANALYSIS.MEDIAADDRESS]?.total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.MEDIAADDRESS]?.total) return;
            onClick(SECTIONS.FILE_ANALYSIS.NETWORK_REFERENCES, {
              state: {
                filterSource: ANALYSIS.MEDIAADDRESS
              }
            });
          }}
          data-testid="report_summary_network_analysis_mediaaddress"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.CUCKOO].total && styles.active
          )}
          keyClassName="font-medium"
          label="Cuckoo Sandbox: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.CUCKOO].malicious}
              total={stats[ANALYSIS.CUCKOO].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.CUCKOO].total) return;
            handleNetworkReferenceClick(
              SECTIONS.DYNAMIC_ANALYSIS.CUCKOO_NETWORK
            );
          }}
          data-testid="report_summary_network_analysis_cuckoo"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.FIREEYE].total && styles.active
          )}
          keyClassName="font-medium"
          label="FireEye Sandbox: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.FIREEYE].malicious}
              total={stats[ANALYSIS.FIREEYE].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.FIREEYE].total) return;
            handleNetworkReferenceClick(
              SECTIONS.DYNAMIC_ANALYSIS.FIREEYE_NETWORK
            );
          }}
          data-testid="report_summary_network_analysis_fireeye"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.JOE].total && styles.active
          )}
          keyClassName="font-medium"
          label="Joe Sandbox: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.JOE].malicious}
              total={stats[ANALYSIS.JOE].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.JOE].total) return;
            handleNetworkReferenceClick(SECTIONS.DYNAMIC_ANALYSIS.JOE_NETWORK);
          }}
          data-testid="report_summary_network_analysis_joe"
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.VMRAY].total && styles.active
          )}
          keyClassName="font-medium"
          label="VMRay: "
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.VMRAY].malicious}
              total={stats[ANALYSIS.VMRAY].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.VMRAY].total) return;
            handleNetworkReferenceClick(
              SECTIONS.DYNAMIC_ANALYSIS.VMRAY_NETWORK
            );
          }}
        />
        <KeyValueList.Item
          className={clsx(
            styles.list_item_network_references,
            stats[ANALYSIS.SOURCES].total && styles.active
          )}
          keyClassName="font-medium"
          label={
            <div className="flex align-center flex-gap-1">
              <span>Sources:</span>
              <RenderWhen condition={isNWParanoidMode}>
                <WarningIcon message="Network references from sources are excluded from Risk Tolerance classification." />
              </RenderWhen>
            </div>
          }
          value={
            <NetworkReferencesValue
              malicious={stats[ANALYSIS.SOURCES].malicious}
              total={stats[ANALYSIS.SOURCES].total}
            />
          }
          onClick={() => {
            if (!stats[ANALYSIS.SOURCES].total) return;
            onClick(SECTIONS.SOURCES.TI_CLOUD);
          }}
          data-testid="report_summary_network_analysis_sources"
        />
      </KeyValueList>
    </Card>
  );
};

export default NetworkReferences;
